import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import {
  listForgePolicies,
  listPeopleForgeAccounts,
  revokeForgeAccount,
  setForgePolicy,
} from '@/lib/api/generated/api';
import type {
  ForgePolicyList,
  PeopleLinksPage,
} from '@/lib/api/generated/model';
import { expectStatus } from '@/lib/api/client';
import { forgeName } from '@/features/identity';
import type { ForgeProvider } from '@/lib/api/generated/model';

import { LINKED_ACCOUNTS_COPY as COPY } from '../linkedAccountsCopy';
import { organizationKeys } from '../queryKeys';

/** The workspaces that require each person's own forge account (spec 160). */
export function useForgePolicies() {
  return useQuery({
    queryKey: organizationKeys.forgePolicies(),
    queryFn: async ({ signal }) =>
      expectStatus(
        await listForgePolicies({ signal }),
        200,
        COPY.policiesFailed,
      ),
    retry: false,
  });
}

/**
 * Require personal accounts in a workspace, or stop. Optimistic: the switch
 * moves at once and goes back, with a toast, if the service refuses.
 */
export function useSetForgePolicy() {
  const queryClient = useQueryClient();
  const key = organizationKeys.forgePolicies();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      required,
    }: {
      workspaceId: string;
      name: string;
      required: boolean;
    }) =>
      expectStatus(
        await setForgePolicy(workspaceId, { required }),
        200,
        COPY.policyFailed,
      ),
    onMutate: async ({ workspaceId, required }) => {
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ForgePolicyList>(key);
      queryClient.setQueryData<ForgePolicyList>(key, (current) => {
        const items = (current?.items ?? []).filter(
          (item) => item.workspace_id !== workspaceId,
        );
        return {
          items: required
            ? [
                ...items,
                {
                  workspace_id: workspaceId,
                  changed_at: new Date().toISOString(),
                },
              ]
            : items,
        };
      });
      return { previous };
    },
    onError: (_error, { name }, context) => {
      queryClient.setQueryData(key, context?.previous);
      toast.error(COPY.policyNotChanged(name));
    },
    onSuccess: (_view, { name, required }) => {
      toast.success(required ? COPY.policyOn(name) : COPY.policyOff(name));
    },
    onSettled: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: key }),
        // The people list names the workspaces that require accounts too.
        queryClient.invalidateQueries({
          queryKey: organizationKeys.peopleLinks(),
        }),
      ]);
    },
  });
}

/** The service's largest page (`people_page_size_max`). */
const DIRECTORY_PAGE_SIZE = 200;
/** A ceiling, so a service that never says it is done cannot loop forever. */
const DIRECTORY_MAX_PAGES = 50;

/**
 * Everyone in the organization and their links, every page read into one
 * list (spec 162 R1): the filters count over the whole organization, and the
 * service has no state filter of its own. Tens to low hundreds of people here,
 * so one or two requests.
 */
export function usePeopleDirectory() {
  return useQuery({
    queryKey: organizationKeys.peopleDirectory(),
    queryFn: async ({ signal }): Promise<PeopleLinksPage> => {
      const first = expectStatus(
        await listPeopleForgeAccounts(
          { page: 1, page_size: DIRECTORY_PAGE_SIZE },
          { signal },
        ),
        200,
        COPY.peopleFailed,
      );
      const items = [...first.items];
      for (
        let page = 2;
        items.length < first.total && page <= DIRECTORY_MAX_PAGES;
        page += 1
      ) {
        const next = expectStatus(
          await listPeopleForgeAccounts(
            { page, page_size: DIRECTORY_PAGE_SIZE },
            { signal },
          ),
          200,
          COPY.peopleFailed,
        );
        if (next.items.length === 0) break;
        items.push(...next.items);
      }
      return { ...first, items, page: 1, page_size: items.length };
    },
    retry: false,
  });
}

/** An admin removes someone's link; the person is told by the service. */
export function useRevokeLink() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      userId,
      host,
    }: {
      userId: string;
      host: string;
      person: string;
      provider: ForgeProvider;
    }) => {
      const response = await revokeForgeAccount(userId, host);
      if (response.status !== 204) throw new Error(COPY.removeFailed);
    },
    onSuccess: async (_result, { person, provider }) => {
      await queryClient.invalidateQueries({
        queryKey: organizationKeys.peopleLinks(),
      });
      toast.success(COPY.removed(person, forgeName(provider)));
    },
  });
}
